import { NavLink, useLocation, useParams } from 'react-router-dom';
import { Activity, BookOpen, Check, Database, Search, Share2 } from 'lucide-react';
import { BucketSearch } from '@/components/BucketSearch';
import { Skeleton } from '@/components/ui';
import { useScopeFacts } from '@/pages/scopeFacts';
import { STAGE_ORDER, TITLES, currentStage, stagesFor, type StageId, type StageStatus } from '@/pages/workflow';
import { scopeNoun, thisScope } from '@/pages/scopeNoun';
import { cn } from '@/lib/cn';

/** Where each stage is worked. Two stages share the packaging page by design. */
const WHERE: Record<StageId, (bucketId: string) => string> = {
  context: (b) => `/buckets/${b}`,
  observe: (b) => `/buckets/${b}`,
  attribute: (b) => `/buckets/${b}#walk`,
  review: (b) => `/buckets/${b}#confirm-tools`,
  package: (b) => `/buckets/${b}/package`,
  deliver: (b) => `/buckets/${b}/package`,
};

const MARK: Record<StageStatus, string> = {
  done: 'border-ok/60 bg-ok/10 text-ok',
  current: 'border-acc bg-acc-soft text-acc-2',
  running: 'border-acc bg-acc-soft text-acc-2',
  blocked: 'border-warn bg-warn-soft text-warn',
  failed: 'border-bad bg-bad-soft text-bad',
  ahead: 'border-line-2 bg-panel-2 text-ink-4',
  unknown: 'border-line-2 bg-panel-2 text-ink-4',
};

/** One entry in the rail's tool list. */
function Tool({ to, label, icon: Icon }: { to: string; label: string; icon: typeof Share2 }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded px-2 py-1.5 text-small text-ink-3 transition-colors duration-fast hover:bg-hov hover:text-ink',
          isActive && 'text-ink',
        )
      }
    >
      <Icon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

/**
 * The rail is the work: which scope, then its six steps, then the utilities.
 *
 * Three things were wrong with what this replaced, and they were one thing.
 * The global utilities sat at the top, above the scope and above the steps, so
 * the first thing on screen was S3 and System status - neither of which is the
 * work. The scope's company hung under the bucket name as an afterthought
 * rather than reading as what it is, the owner of the thing you are in. And the
 * six steps were drawn on the page, twice, while the rail - the one part of the
 * screen that exists to navigate - listed surfaces that did not say they were
 * ordered at all.
 *
 * So: the scope reads company first and bucket second, because that is the
 * address. The steps are numbered, carry their own state, and are the rail's
 * body, so the flow is visibly a flow and every step is a place you can go.
 * Utilities are at the bottom, under a line, where a tool that is not part of
 * this bucket's work belongs.
 */
export function ScopeRail() {
  const { bucketId, companyId } = useParams();
  const { hash, pathname } = useLocation();
  // The rail's own facts already read this bucket, and this fetched every
  // registered bucket in the account beside them - the whole catalogue, on
  // every screen, to print one name that the bucket's own record carries.
  const scope = useScopeFacts(bucketId, companyId);

  const stages = bucketId ? stagesFor(scope.facts) : [];
  const at = bucketId ? currentStage(stages).id : null;

  /** Which step the current URL is showing, so the rail marks a place, not a guess. */
  const showing: StageId | null = !bucketId
    ? null
    : pathname.endsWith('/package')
      ? stages.find((s) => s.id === 'deliver')?.status === 'current'
        ? 'deliver'
        : 'package'
      : hash === '#confirm-tools'
        ? 'review'
        : hash === '#walk'
          ? 'attribute'
          : at;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* The name, and nothing else. Region and account are how the storage
          is addressed internally, not what an operator selected. */}
      <div className="border-t border-line px-4 pb-3 pt-3.5">
        {bucketId && scope.naming ? (
          <Skeleton className="h-6 w-full" />
        ) : bucketId ? (
          <p className="mono max-w-full truncate text-row font-medium text-ink">
            {scope.bucketName ?? thisScope(scope.prefix)}
          </p>
        ) : (
          <p className="text-small text-ink-3">No bucket chosen</p>
        )}
      </div>

      <div className="px-3 pb-3">
        <BucketSearch />
      </div>

      {/* The six steps, in order, as the rail's body. */}
      {bucketId && (
        <nav aria-label={`Stages of ${thisScope(scope.prefix)}`} className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
          <p className="px-1 pb-1.5 text-caption uppercase tracking-wider text-ink-4">Stages</p>
          {STAGE_ORDER.map((id, i) => {
            const stage = stages.find((s) => s.id === id);
            const status = stage?.status ?? 'unknown';
            return (
              <NavLink
                key={id}
                to={WHERE[id](bucketId)}
                className={cn(
                  'seam flex w-full items-center gap-2.5 rounded-r px-2 py-2 text-small transition-colors duration-fast hover:bg-hov',
                  showing === id ? 'seam-on text-ink' : 'text-ink-2 hover:text-ink',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'mono grid h-5 w-5 flex-none place-items-center rounded-full border text-caption leading-none',
                    MARK[status],
                  )}
                >
                  {status === 'done' ? <Check className="h-2.5 w-2.5" /> : i + 1}
                </span>
                <span className="min-w-0 flex-1 truncate">{TITLES[id].title}</span>
                {status === 'running' && <span className="flex-none text-caption text-acc-2">running</span>}
                {status === 'blocked' && <span className="flex-none text-caption text-warn">blocked</span>}
              </NavLink>
            );
          })}
        </nav>
      )}

      {!bucketId && (
        <p className="min-h-0 flex-1 px-4 py-2 text-caption text-ink-4">
          Choose a bucket to work in. Everything after that is scoped to it.
        </p>
      )}

      {/* Under a line, at the end, grouped by the level each one reads.

          These used to be one fixed list under every screen - the account's
          whole S3 catalogue, a record search that picked a bucket for you, and
          the system's status - so the way out of a bucket was permanently the
          last thing on the rail. Making the list swap on `bucketId` fixed that
          and broke something worse: these are the app's ONLY links to /s3 and
          /operations, so inside a bucket both pages became unreachable and the
          only exit was the wordmark. Level-awareness is worth keeping; being
          the sole route to a page and then withdrawing it is not.

          So the bucket's tools appear when there is a bucket, and the
          account's are always there, under their own label. */}
      <nav aria-label="Tools" className="border-t border-line px-3 py-2.5">
        {bucketId && (
          <>
            {/* A scope is (account, bucket, prefix). Half of them are folders,
                and calling one "This bucket" under a rail that correctly names
                it `bucket/folder` is the label and the prose disagreeing. */}
            <p className="px-1 pb-1 pt-0.5 text-caption uppercase tracking-wider text-ink-4">
              This {scopeNoun(scope.prefix)}
            </p>
            {[
              { to: `/buckets/${bucketId}/graph`, label: 'Live graph', icon: Share2 },
              { to: `/buckets/${bucketId}/walkthrough`, label: 'Browse the records', icon: BookOpen },
              { to: `/buckets/${bucketId}/search`, label: `Search ${thisScope(scope.prefix)}`, icon: Search },
            ].map((t) => (
              <Tool key={t.to} {...t} />
            ))}
          </>
        )}
        <p className={cn('px-1 pb-1 text-caption uppercase tracking-wider text-ink-4', bucketId ? 'pt-2.5' : 'pt-0.5')}>
          The account
        </p>
        {[
          { to: '/s3', label: 'All S3 buckets', icon: Database },
          { to: '/operations', label: 'System status', icon: Activity },
        ].map((t) => (
          <Tool key={t.to} {...t} />
        ))}
      </nav>
    </div>
  );
}
